import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { HistoryClient, TemporalRequest } from "../../entities/NSwagGenerated"
import { useErrorToaster } from "@/composables/toaster"
import { useTemporalDiff } from "@/composables/useTemporalDiff"
import { toApiConfig } from "@/models/temporalTypes"
import type { TemporalRelationConfig, TemporalDisplayConfig, TemporalLoadRequest } from "@/models/temporalTypes"

export const useTemporalHistoryStore = defineStore("temporalHistory", () => {
	const isOpen = ref(false)
	const title = ref("")
	const loading = ref(false)
	const hasQueried = ref(false)
	const rawData = ref<Record<string, any[]>>({})
	const config = ref<TemporalRelationConfig | null>(null)
	const displayConfig = ref<TemporalDisplayConfig>({})
	const filter = ref<Record<string, string>>({})
	const dateRange = ref<{ from: Date; to: Date } | null>(null)

	const { changeEvents: allChangeEvents } = useTemporalDiff(rawData, displayConfig)

	const changeEvents = computed(() => {
		if (!dateRange.value) return allChangeEvents.value
		const from = dateRange.value.from.getTime()
		const to = dateRange.value.to.getTime()
		return allChangeEvents.value.filter((e) => {
			const t = new Date(e.timestamp).getTime()
			return t >= from && t <= to
		})
	})

	async function fetchData(loadRequest: TemporalLoadRequest = { mode: "all" }) {
		if (!config.value) return
		loading.value = true
		hasQueried.value = true
		rawData.value = {}
		try {
			const client = new HistoryClient(import.meta.env.VITE_API_URL?.toString())
			const body = new TemporalRequest({ filter: filter.value, config: toApiConfig(config.value) })
			if (loadRequest.mode === "all") {
				dateRange.value = null
				rawData.value = await client.all(body)
			} else if (loadRequest.mode === "asOf") {
				dateRange.value = null
				rawData.value = await client.asOf(loadRequest.asOf, body)
			} else {
				dateRange.value = { from: loadRequest.from, to: loadRequest.to }
				// Temporary fix: fetch full history so useTemporalDiff sees complete adjacency and
				// classifies created/updated/deleted correctly, then filter to the window below.
				// FOR SYSTEM_TIME BETWEEN drops the rows this classification needs. See
				// temporal-history-classification.md for the proper server-side fix.
				rawData.value = await client.all(body)
			}
		} catch (err) {
			useErrorToaster(err)
		} finally {
			loading.value = false
		}
	}

	async function loadHistory(
		_config: TemporalRelationConfig,
		_displayConfig: TemporalDisplayConfig,
		_filter: Record<string, string>,
		_title: string,
	) {
		config.value = _config
		displayConfig.value = _displayConfig
		filter.value = _filter
		title.value = _title
		isOpen.value = true
		await fetchData()
	}

	async function reload(loadRequest: TemporalLoadRequest = { mode: "all" }) {
		await fetchData(loadRequest)
	}

	function close() {
		isOpen.value = false
		rawData.value = {}
		dateRange.value = null
		hasQueried.value = false
	}

	return { isOpen, title, loading, hasQueried, changeEvents, loadHistory, reload, close }
})
